import { ApiChartType, ApiChartTemplate } from "@incendium/api";
import {
  BubbleChart,
  Dashboard,
  HexagonOutlined,
  InsertChart,
  Map,
  PictureInPicture,
  PieChart,
  TableChart,
} from "@mui/icons-material";
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import produce from "immer";
import { useReportBuilderContext } from "Providers/ReportBuilderProvider";
import { AccordianChartBuilderSidebarBlock } from "./ChartBuilderSidebarBlock";

interface IChartBuilderChartTypeProps {
  types?: ApiChartType[];
}

const chartTypeDescriptions = {
  [ApiChartType.TABLE]: {
    name: "Table Chart",
    description: "Use for data table",
    icon: <TableChart />,
  },
  [ApiChartType.GRAPH]: {
    name: "Graph Chart",
    description: "Use the graph chart to visualize trends of any metric in column, stacked, line and area graph form.",
    icon: <InsertChart />,
  },
  [ApiChartType.PIE]: {
    name: "Pie Chart",
    description: "Use the pie chart to visualize compositions of segmented dimensions paired with any metrics.",
    icon: <PieChart />,
  },
  [ApiChartType.BUBBLE]: {
    name: "Bubble Chart",
    description: "Set a dimension and 3 metrics, the last metric will be the bubble size",
    icon: <BubbleChart />,
  },
  [ApiChartType.RADAR]: {
    name: "Radar Chart",
    description: "Use radar charts to plot variables as axes, with data points forming polygons to visually represent the dataset.",
    icon: <HexagonOutlined />,
  },
  [ApiChartType.TREE_MAP]: {
    name: "Treemap",
    description: "Utilize this graph for hierarchical visualization and identifying outliers within your dataset.",
    icon: <Dashboard />,
  },
  [ApiChartType.STAT_CARD]: {
    name: "Statcard",
    description: "Give your report a headline figure.",
    icon: <PictureInPicture />,
  },
  [ApiChartType.MAP]: {
    name: "Map",
    description: "Use the map to visualize data on a world map.",
    icon: <Map />,
  },
}

function ChartBuilderChartType({ types }: IChartBuilderChartTypeProps) {
  const { chart, setChart } = useReportBuilderContext();


  return (
    <AccordianChartBuilderSidebarBlock title="Chart Type">
      <Stack spacing={2}>
        <ToggleButtonGroup
          size="small"
          value={chart.type}
          exclusive
          color="secondary"
          onChange={(e, v: ApiChartType) => {
            setChart((chart) =>
              produce(chart, (draft) => {
                if (!v) {
                  return;
                }

                if (v === ApiChartType.MAP) {
                  draft.type = v;
                  draft.template = ApiChartTemplate.MAP;
                  return
                }

                draft.type = v;
                if (
                  !draft.template ||
                  draft.template === ApiChartTemplate.UNDEFINED ||
                  draft.template === ApiChartTemplate.MAP
                ) {
                  draft.template =
                    v === ApiChartType.TABLE
                      ? ApiChartTemplate.TABLE
                      : ApiChartTemplate.SNAPSHOT;
                }
              })
            );
          }}
        >
          {[
            ApiChartType.TABLE,
            ApiChartType.GRAPH,
            ApiChartType.PIE,
            ApiChartType.BUBBLE,
            ApiChartType.RADAR,
            ApiChartType.TREE_MAP,
            ApiChartType.STAT_CARD,
            ApiChartType.MAP,
          ]
            .filter((t) => !types || types.includes(t as ApiChartType))
            .map((t) => (
              <Tooltip arrow key={t} title={<>
                <Typography color="inherit" variant="subtitle2">{chartTypeDescriptions[t].name}</Typography>
                <Typography color="inherit" variant="body2">{chartTypeDescriptions[t].description}</Typography>
              </>}>
                <ToggleButton value={t}>
                  {chartTypeDescriptions[t].icon}
                </ToggleButton>
              </Tooltip>
            ))}
        </ToggleButtonGroup>

        <Typography variant="body2">
          {chartTypeDescriptions[chart.type as ApiChartType].name}: {chartTypeDescriptions[chart.type as ApiChartType].description}
        </Typography>
      </Stack>
    </AccordianChartBuilderSidebarBlock>
  );
}

export default ChartBuilderChartType;
